import React, { useState, useEffect } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db, storage } from "./firebase";
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import "./CompetitionForm.css";

// TEMP: Firebase Storage quota exceeded. Set to true to restore direct file uploads.
const FILE_UPLOADS_ENABLED = false;

export default function ICTSocietyForm({ onClose }) {
  const [formData, setFormData] = useState({
    schoolName: "",
    position: "",
    clubEmail: "",
    contactPerson: "",
    contactPhone: "",
    schoolLogo: null, // file object
    logoLink: "", // Google Drive link (temporary replacement for upload)
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  // cleanup preview URL on unmount / change
  useEffect(() => {
    return () => {
      if (logoPreview) URL.revokeObjectURL(logoPreview);
    };
  }, [logoPreview]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSubmitStatus(null);
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSubmitStatus(null);

    if (file) {
      const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml"];
      if (!validTypes.includes(file.type)) {
        setSubmitStatus({
          type: "error",
          message: (
            <>
              Upload PNG, JPG, JPEG, or SVG — if this persists <a href="/contact">contact us</a>.
            </>
          ),
        });
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        setSubmitStatus({
          type: "error",
          message: (
            <>
              File must be &lt; 2MB — if this persists <a href="/contact">contact us</a>.
            </>
          ),
        });
        return;
      }

      // revoke previous preview URL
      if (logoPreview) URL.revokeObjectURL(logoPreview);
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);

      setFormData((prev) => ({ ...prev, schoolLogo: file }));
    }
  };

  const uploadFileToStorage = (file) => {
    return new Promise((resolve, reject) => {
      const path = `ict_society_logos/${Date.now()}_${file.name}`;
      const sRef = storageRef(storage, path);
      const uploadTask = uploadBytesResumable(sRef, file);

      uploadTask.on(
        "state_changed",
        // progress - optional
        () => {},
        (err) => reject(err),
        async () => {
          try {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(url);
          } catch (e) {
            reject(e);
          }
        }
      );
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    const linkOk = /^https?:\/\//i.test((formData.logoLink || "").trim());
    const logoMissing = FILE_UPLOADS_ENABLED ? !formData.schoolLogo : !linkOk;

    if (!formData.schoolName || !formData.position || !formData.clubEmail || logoMissing) {
      setSubmitStatus({
        type: "error",
        message: (
          <>
            Fill all required fields including a valid logo {FILE_UPLOADS_ENABLED ? "file" : "Google Drive link (https://...)"} — if this persists <a href="/contact">contact us</a>.
          </>
        ),
      });
      setIsSubmitting(false);
      return;
    }

    try {
      // Upload logo to Firebase Storage (or use the Drive link while uploads are disabled)
      const downloadURL = FILE_UPLOADS_ENABLED
        ? await uploadFileToStorage(formData.schoolLogo)
        : formData.logoLink.trim();

      // Submit Firestore doc with logo URL (no base64)
      await addDoc(collection(db, "ict_society_registrations"), {
        competitionTitle: "Most Popular ICT Society",
        schoolName: formData.schoolName,
        position: formData.position,
        clubEmail: formData.clubEmail,
        contactPerson: formData.contactPerson || "",
        contactPhone: formData.contactPhone || "",
        logoUrl: downloadURL,
        timestamp: serverTimestamp(),
        status: "pending",
      });

      setSubmitStatus({ type: "success", message: "Registration submitted successfully!" });
      setTimeout(onClose, 3000);
    } catch (error) {
      console.error(error);
      setSubmitStatus({
        type: "error",
        message: (
          <>
            An error occurred — if this persists <a href="/contact">contact us</a>.
          </>
        ),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="overlay-container">
      <div className="form-container" style={{ marginTop: "5%" }}>
        <button className="form-close-btn" onClick={onClose}>×</button>
        <h2>Most Popular ICT Society Registration</h2>
        <p>Register your school's ICT society</p>

        <form onSubmit={handleSubmit}>
          <label>School Name <span className="required">*</span></label>
          <input name="schoolName" value={formData.schoolName} onChange={handleChange} placeholder="Enter school name" required />

          <label>Position <span className="required">*</span></label>
          <select name="position" value={formData.position} onChange={handleChange} required>
            <option value="">Select your position</option>
            <option>Chairman</option>
            <option>Secretary</option>
            <option>Treasurer</option>
            <option>Assistant Chairman</option>
          </select>

          <label>Club Email <span className="required">*</span></label>
          <input type="email" name="clubEmail" value={formData.clubEmail} onChange={handleChange} placeholder="club@yourschool.edu" required />

          <label>Contact Person</label>
          <input name="contactPerson" value={formData.contactPerson} onChange={handleChange} placeholder="Full name" />

          <label>Contact Phone</label>
          <input name="contactPhone" value={formData.contactPhone} onChange={handleChange} placeholder="+94 XX XXX XXXX" />

          {FILE_UPLOADS_ENABLED ? (
            <>
              <label>School Logo <span className="required">*</span></label>
              <input type="file" name="schoolLogo" onChange={handleFileChange} accept="image/png,image/jpeg,image/jpg,image/svg+xml" required />
              {logoPreview && <div className="logo-preview"><img src={logoPreview} alt="School Logo Preview" /></div>}
            </>
          ) : (
            <>
              <label>School Logo - Google Drive link <span className="required">*</span></label>
              <input name="logoLink" value={formData.logoLink} onChange={handleChange} placeholder="https://drive.google.com/... (sharing: Anyone with the link)" required />
            </>
          )}

          {submitStatus && <div className={`submit-status ${submitStatus.type}`}>{submitStatus.message}</div>}

          <div style={{ display: "flex", justifyContent: "space-between", marginTop: "20px" }}>
            <button type="button" onClick={onClose} className="btn">Cancel</button>
            <button type="submit" className="btn">{isSubmitting ? "Submitting..." : "Submit Registration"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
